
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Download,
  Eye,
  FileCheck2,
  FileImage,
  FileText,
  GraduationCap,
  ShieldCheck,
  X,
} from "lucide-react";

const documents = [
  {
    id: 1,
    title: "St. Xavier Public School Bela",
    pdfUrl: "logo.png",
    type: "image",
    description: "School information and identity",
  },
  {
    id: 2,
    title: "School Affiliation Certificate",
    pdfUrl: "Doc10.pdf",
    type: "pdf",
    description: "School affiliation documentation",
  },
  {
    id: 3,
    title: "School Recognition Certificate",
    pdfUrl: "Doc7.pdf",
    type: "pdf",
    description: "School recognition documentation",
  },
  {
    id: 4,
    title: "School Building Safety Certificate",
    pdfUrl: "Doc11.pdf",
    type: "pdf",
    description: "Building safety documentation",
  },
];

const Admission = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    if (!selectedDoc) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedDoc(null);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [selectedDoc]);

  const handleView = (doc) => {
    setSelectedDoc(doc);
  };

  const closeModal = () => {
    setSelectedDoc(null);
  };

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-16">

      {/* PAGE HEADER */}
      <div className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-4 pb-24 pt-12 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[36px] border-white/5" />

        <div className="pointer-events-none absolute bottom-0 left-1/3 h-44 w-44 rounded-full bg-amber-300/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto max-w-6xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-2 text-[11px] font-normal tracking-wide text-amber-100">
            <GraduationCap size={16} />
            School Information & Documents
          </span>

          <h1 className="mt-5 font-serif text-[32px] font-medium leading-tight sm:text-[42px] lg:text-[48px]">
            Important{" "}
            <span className="text-amber-300">
              Documents
            </span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-white/85">
            Access school certificates and important
            documents in one convenient place.
            View documents online or download them
            for your reference.
          </p>
        </motion.div>
      </div>

      {/* BACKGROUND DECORATION */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-28 top-[380px] h-72 w-72 rounded-full bg-red-100/60 blur-3xl" />
        <div className="absolute -right-28 bottom-0 h-80 w-80 rounded-full bg-amber-100/60 blur-3xl" />
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 mx-auto -mt-12 max-w-6xl px-4 sm:px-6 lg:px-8">

        {/* INTRO STRIP */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-red-100 bg-white px-5 py-4 shadow-[0_8px_30px_rgba(80,30,30,0.06)] sm:px-6">

          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-800">
              <FileText size={21} />
            </div>

            <div>
              <h2 className="font-serif text-lg font-medium text-slate-900">
                Document Library
              </h2>

              <p className="mt-1 text-xs text-slate-500">
                Browse the available school documents
              </p>
            </div>
          </div>

          <span className="rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5 text-xs font-normal text-amber-800">
            {documents.length} Documents
          </span>
        </div>

        {/* DOCUMENT GRID */}
        <div className="grid gap-5 md:grid-cols-2">

          {documents.map((doc, index) => {
            const Icon =
              doc.type === "image"
                ? FileImage
                : FileCheck2;

            return (
              <motion.article
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.07,
                }}
                className="group relative overflow-hidden rounded-[22px] border border-red-100 bg-white p-5 shadow-[0_10px_35px_rgba(80,30,30,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-red-200 hover:shadow-[0_18px_45px_rgba(80,30,30,0.12)] sm:p-6"
              >
                {/* ACCENT */}
                <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-red-800 to-amber-400" />

                <div className="flex items-start gap-4">

                  {/* DOCUMENT ICON */}
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-red-100 bg-red-50 text-red-800">
                    <Icon size={26} strokeWidth={1.6} />
                  </div>

                  {/* DOCUMENT INFORMATION */}
                  <div className="min-w-0 flex-1">

                    <span className="text-[11px] font-normal tracking-wide text-amber-700">
                      DOCUMENT {String(index + 1).padStart(2, "0")}
                    </span>

                    <h3 className="mt-1 font-serif text-[18px] font-medium leading-snug text-slate-900 sm:text-[20px]">
                      {doc.title}
                    </h3>

                    <p className="mt-2 text-[13px] font-normal leading-6 text-slate-500">
                      {doc.description}
                    </p>

                    <div className="mt-4 flex flex-wrap items-center gap-3">

                      <button
                        type="button"
                        onClick={() => handleView(doc)}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-800 px-4 py-2.5 text-xs font-medium text-white transition hover:bg-red-900"
                      >
                        <Eye size={15} />
                        View Document
                      </button>

                      <a
                        href={doc.pdfUrl}
                        download
                        className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2.5 text-xs font-medium text-red-800 transition hover:bg-red-50"
                      >
                        <Download size={15} />
                        Download
                      </a>

                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* INFORMATION NOTE */}
        <div className="mt-7 flex items-start gap-3 rounded-xl border-l-4 border-amber-400 bg-white px-5 py-4 shadow-sm">
          <ShieldCheck
            size={19}
            className="mt-0.5 shrink-0 text-amber-600"
          />

          <p className="text-xs font-normal leading-6 text-slate-600">
            These documents are provided for school
            information and reference. Select View Document
            to preview a file, or use Download to save
            a copy.
          </p>
        </div>
      </div>

      {/* DOCUMENT PREVIEW MODAL */}
      <AnimatePresence>
        {selectedDoc && (
          <motion.div
            key="document-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm sm:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeModal();
              }
            }}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="document-modal-title"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.2 }}
              className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[20px] bg-white shadow-2xl"
            >

              {/* MODAL HEADER */}
              <div className="flex shrink-0 items-center justify-between gap-4 bg-gradient-to-r from-red-900 to-red-700 px-4 py-4 text-white sm:px-6">

                <div className="flex min-w-0 items-center gap-3">
                  <FileText
                    size={20}
                    className="shrink-0 text-amber-300"
                  />

                  <h3
                    id="document-modal-title"
                    className="truncate font-serif text-sm font-medium sm:text-lg"
                  >
                    {selectedDoc.title}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={closeModal}
                  aria-label="Close preview"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 transition hover:bg-white/20"
                >
                  <X size={19} />
                </button>
              </div>

              {/* MODAL PREVIEW */}
              <div className="min-h-0 flex-1 overflow-auto bg-slate-100 p-3 sm:p-4">
                {selectedDoc.type === "image" ? (
                  <div className="flex h-full min-h-[300px] items-center justify-center rounded-xl bg-white p-5">
                    <img
                      src={selectedDoc.pdfUrl}
                      alt={selectedDoc.title}
                      className="max-h-[65vh] max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <iframe
                    src={selectedDoc.pdfUrl}
                    title={selectedDoc.title}
                    className="h-[65vh] min-h-[300px] w-full rounded-xl border-0 bg-white"
                  />
                )}
              </div>

              {/* MODAL FOOTER */}
              <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-white px-4 py-3 sm:px-6">
                <span className="text-xs font-normal text-slate-500">
                  Document Preview
                </span>

                <div className="flex items-center gap-2">
                  <a
                    href={selectedDoc.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-800 hover:bg-red-50"
                  >
                    <ArrowUpRight size={15} />
                    Open File
                  </a>

                  <a
                    href={selectedDoc.pdfUrl}
                    download
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-800 px-3 py-2 text-xs font-medium text-white transition hover:bg-red-900"
                  >
                    <Download size={15} />
                    Download
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Admission;
